/**
 * Yahoo Finance Service
 *
 * Uses the public v8/finance/chart endpoint — no API key required.
 * This endpoint is undocumented but has been reliably available for years.
 *
 * What it provides (no key needed):
 *   - Real-time/delayed price quote (price, change, %, volume, 52-week range)
 *   - Company name, exchange, currency
 *   - Historical OHLCV price series (up to 10 years, daily/weekly/monthly)
 *
 * What it does NOT provide:
 *   - Financial statements (income, balance sheet, cash flow)
 *   - Those still require Alpha Vantage or CSV upload
 *
 * Rate limits: generous (no documented cap), but we cache aggressively.
 */

import { getCachedPrice, setCachedPrice } from './stockDb';
import type { PriceQuote } from '@/types/stock';

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';
const YF_BASE = 'https://query2.finance.yahoo.com/v8/finance/chart';

function proxied(url: string): string {
  return `${CORS_PROXY}${encodeURIComponent(url)}`;
}

// ============================================================
// Types
// ============================================================

export interface YahooQuote {
  ticker: string;
  name: string;
  exchange: string;
  currency: string;
  price: number;
  previousClose: number;
  change: number;
  changePercent: number;
  volume: number;
  regularMarketDayHigh: number;
  regularMarketDayLow: number;
  fiftyTwoWeekHigh: number;
  fiftyTwoWeekLow: number;
  marketCap: number;
  timestamp: number;
}

export interface HistoricalPrice {
  date: string;       // YYYY-MM-DD
  timestamp: number;  // unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
  adjClose: number;
  volume: number;
}

export type YahooInterval = '1d' | '1wk' | '1mo' | '3mo';
export type YahooRange = '1mo' | '3mo' | '6mo' | '1y' | '2y' | '5y' | '10y' | 'ytd' | 'max';

// ============================================================
// In-memory session cache
// ============================================================

const SESSION_QUOTES = new Map<string, YahooQuote>();
const SESSION_HISTORY = new Map<string, HistoricalPrice[]>();

// ============================================================
// Core fetch
// ============================================================

async function yfFetch(ticker: string, interval: YahooInterval, range: YahooRange): Promise<Record<string, unknown>> {
  const url = `${YF_BASE}/${encodeURIComponent(ticker)}?interval=${interval}&range=${range}`;
  const res = await fetch(proxied(url));
  if (!res.ok) {
    if (res.status === 404) throw new Error(`Yahoo Finance: ticker "${ticker}" not found.`);
    if (res.status === 429) throw new Error('Yahoo Finance: too many requests, please wait a moment.');
    throw new Error(`Yahoo Finance HTTP ${res.status}`);
  }
  const json = await res.json() as { chart: { result: Array<Record<string, unknown>> | null; error: { description: string } | null } };
  if (json.chart.error) throw new Error(`Yahoo Finance: ${json.chart.error.description}`);
  if (!json.chart.result?.length) throw new Error(`Yahoo Finance: no data returned for "${ticker}".`);
  return json.chart.result[0];
}

// ============================================================
// Live quote  (uses 1d range — single candle = today's data)
// ============================================================

export async function yahooGetQuote(ticker: string): Promise<YahooQuote> {
  const t = ticker.toUpperCase();

  // Session cache
  const sessionHit = SESSION_QUOTES.get(t);
  if (sessionHit) return sessionHit;

  // IndexedDB 15-min cache
  const dbHit = await getCachedPrice(t);
  if (dbHit) {
    const q = dbHitToYahooQuote(t, dbHit);
    SESSION_QUOTES.set(t, q);
    return q;
  }

  const result = await yfFetch(t, '1d', '1d');
  const meta = result['meta'] as Record<string, unknown>;

  const price = Number(meta['regularMarketPrice'] ?? 0);
  const prevClose = Number(meta['chartPreviousClose'] ?? meta['previousClose'] ?? price);
  const change = price - prevClose;

  const quote: YahooQuote = {
    ticker: t,
    name: String(meta['longName'] ?? meta['shortName'] ?? t),
    exchange: String(meta['fullExchangeName'] ?? meta['exchangeName'] ?? ''),
    currency: String(meta['currency'] ?? 'USD'),
    price,
    previousClose: prevClose,
    change,
    changePercent: prevClose !== 0 ? (change / prevClose) * 100 : 0,
    volume: Number(meta['regularMarketVolume'] ?? 0),
    regularMarketDayHigh: Number(meta['regularMarketDayHigh'] ?? 0),
    regularMarketDayLow: Number(meta['regularMarketDayLow'] ?? 0),
    fiftyTwoWeekHigh: Number(meta['fiftyTwoWeekHigh'] ?? 0),
    fiftyTwoWeekLow: Number(meta['fiftyTwoWeekLow'] ?? 0),
    marketCap: 0, // not in chart meta
    timestamp: Date.now(),
  };

  SESSION_QUOTES.set(t, quote);

  // Also store in the shared PriceQuote cache (15-min TTL)
  await setCachedPrice(t, {
    ticker: t,
    price: quote.price,
    change: quote.change,
    changePercent: quote.changePercent,
    volume: quote.volume,
    marketCap: 0,
    timestamp: quote.timestamp,
  });

  return quote;
}

function dbHitToYahooQuote(ticker: string, p: PriceQuote): YahooQuote {
  return {
    ticker,
    name: ticker,
    exchange: '',
    currency: 'USD',
    price: p.price,
    previousClose: p.price - p.change,
    change: p.change,
    changePercent: p.changePercent,
    volume: p.volume,
    regularMarketDayHigh: 0,
    regularMarketDayLow: 0,
    fiftyTwoWeekHigh: 0,
    fiftyTwoWeekLow: 0,
    marketCap: p.marketCap,
    timestamp: p.timestamp,
  };
}

// ============================================================
// Historical prices
// ============================================================

export async function yahooGetHistory(
  ticker: string,
  interval: YahooInterval = '1mo',
  range: YahooRange = '10y',
): Promise<HistoricalPrice[]> {
  const t = ticker.toUpperCase();
  const cacheKey = `${t}:${interval}:${range}`;

  const sessionHit = SESSION_HISTORY.get(cacheKey);
  if (sessionHit) return sessionHit;

  const result = await yfFetch(t, interval, range);

  const timestamps = (result['timestamp'] as number[] | undefined) ?? [];
  const indicators = result['indicators'] as Record<string, unknown> | undefined;
  const quoteArr = (indicators?.['quote'] as Array<Record<string, number[]>> | undefined)?.[0] ?? {};
  const adjCloseArr = (indicators?.['adjclose'] as Array<Record<string, number[]>> | undefined)?.[0]?.['adjclose'] ?? [];

  const prices: HistoricalPrice[] = timestamps.map((ts, i) => {
    const d = new Date(ts * 1000);
    return {
      date: d.toISOString().slice(0, 10),
      timestamp: ts,
      open: quoteArr['open']?.[i] ?? 0,
      high: quoteArr['high']?.[i] ?? 0,
      low: quoteArr['low']?.[i] ?? 0,
      close: quoteArr['close']?.[i] ?? 0,
      adjClose: adjCloseArr[i] ?? quoteArr['close']?.[i] ?? 0,
      volume: quoteArr['volume']?.[i] ?? 0,
    };
  }).filter((p) => p.close > 0);

  SESSION_HISTORY.set(cacheKey, prices);
  return prices;
}

// ============================================================
// Batch quotes for watchlist
// ============================================================

export async function yahooGetQuotes(tickers: string[]): Promise<Map<string, YahooQuote>> {
  const results = new Map<string, YahooQuote>();
  // Fetch sequentially with a small gap to be polite
  for (const ticker of tickers) {
    try {
      const quote = await yahooGetQuote(ticker);
      results.set(ticker.toUpperCase(), quote);
      // Small delay between requests to avoid rate limiting
      await new Promise<void>((resolve) => setTimeout(resolve, 300));
    } catch {
      // Skip failed tickers silently
    }
  }
  return results;
}

// ============================================================
// Normalise historical prices to percentage returns
// (for benchmark comparison charts)
// ============================================================

export function toPercentReturns(prices: HistoricalPrice[]): Array<{ date: string; return: number }> {
  if (prices.length === 0) return [];
  const base = prices[0].adjClose;
  return prices.map((p) => ({
    date: p.date,
    return: base !== 0 ? ((p.adjClose - base) / base) * 100 : 0,
  }));
}
